<template>
  <div>
    <div v-if="loadingFare" class="flex items-center justify-center h-full mt-5">
      <div class="py-16">
        <div class="spinner mb-8 h-8 font-xs" style="font-size: 0.5rem"></div>
        <p class="text-sm font-bold text-dark-type-3">Computing trip fare...</p>
      </div>

    </div>
    <template v-else>
      <div
        class="
        flex flex-col
        items-center
        justify-center
        w-full
        h-24
        py-2
        rounded rounded-t rounded-b-none rounded-l-none rounded-r-none
        bg-active
      "
      >
        <div class="text-sm font-medium text-white uppercase">Total</div>
        <div class="mt-2 text-3xl font-bold leading-7 text-white">
          &#8358; {{ paymentData.total }}
        </div>
      </div>
      <div class="px-4 mt-6 text-sm font-bold">How to use our tickets</div>
      <div class="px-2 mt-4 bg-white">
        <div class="flex px-2 pt-5 pb-3 border-b">
          <div class="mt-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18">
              <g fill="none" fill-rule="evenodd" transform="translate(-7 -7)">
                <circle cx="16" cy="16" r="16" fill="none" />
                <path
                  stroke="#728881"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12.444 8H9.778C8.796 8 8 8.796 8 9.778v2.666m16 0V9.778C24 8.796 23.204 8 22.222 8h-2.666m0 16h2.666c.982 0 1.778-.796 1.778-1.778v-2.666m-16 0v2.666C8 23.204 8.796 24 9.778 24h2.666"
                />
              </g>
            </svg>
          </div>
          <div class="ml-3">
            <div class="text-xs font-bold text-dark-type-3">Scan QR code</div>
            <div class="mt-1 font-medium text-xxs">
              Scan your QR code before entering the shuttle.
            </div>
          </div>
        </div>
        <div class="flex px-2 pt-5 pb-3">
          <div class="mt-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20">
              <g fill="none" fill-rule="evenodd" transform="translate(-8 -6)">
                <circle cx="16" cy="16" r="16" fill="none" />
                <path
                  stroke="#728881"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M23 25l-7-5-7 5V9a2 2 0 012-2h10a2 2 0 012 2v16z"
                />
              </g>
            </svg>
          </div>
          <div class="ml-3">
            <div class="text-xs font-bold text-dark-type-3">Ticket number</div>
            <div class="mt-1 font-medium text-xxs">
              Give your ticket number to the bus official.
            </div>
          </div>
        </div>
      </div>
      <div class="px-4 pb-10">
        <div class="mt-4 text-sm font-bold text-dark-type-3">
          Booking breakdown
        </div>
        <div class="flex items-center justify-between mt-6">
          <div class="text-xs font-medium text-dark-type-4">Ticket type</div>
          <div class="text-xs font-medium text-dark-type-3">
            {{ paymentData.ticketType }}
          </div>
        </div>
        <div class="flex items-center justify-between mt-4">
          <div class="text-xs font-medium text-dark-type-4">Passengers</div>
          <div class="text-xs font-medium text-dark-type-3">
            {{ paymentData.passengers }}
          </div>
        </div>
        <div class="flex items-center justify-between mt-4">
          <div class="text-xs font-medium text-dark-type-4">
            Price per passenger
          </div>
          <div class="text-xs font-medium text-dark-type-3">
            <span v-if="!loadingFare">&#8358; {{ paymentData.pricePerPassenger }}</span>
            <span v-else>Loading fare</span>
          </div>
        </div>
        <div class="flex items-center justify-between mt-4">
          <div class="text-xs font-bold text-dark-type-3">Total</div>
          <div class="text-xs font-bold text-dark-type-3">
            <span v-if="!loadingFare">&#8358; {{ paymentData.total }}</span>
            <span v-else>Loading fare</span>
          </div>
        </div>
        <hr class="my-4" />

        <div class="mt-4" v-if="favouriteBooking">
          <div class="w-full">
            <div class="relative inline-block w-full date-picker-control">
              <flat-pickr
                v-model="departureDate"
                placeholder="Choose new departure date"
                :config="{ minDate: 'today' }"
                class="
                block
                w-full
                h-full
                px-2
                py-2
                pr-8
                text-sm
                font-medium
                leading-tight
                rounded
                appearance-none
                focus:outline-none
                flat-picker
                placeholder-label
                bg-gray-type-1
              "
                required
                @on-change="emitUpdatedDate"
              ></flat-pickr>
              <div
                class="
                absolute
                inset-y-0
                right-0
                flex
                items-center
                px-1
                pointer-events-none
              "
              >
                <svg
                  class="w-6 h-6 fill-current grey-chevron"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <button
          @click="maybeTriggerPaymentModal(paymentData)"
          class="
          block
          w-full
          py-4
          mt-10
          font-bold
          text-center text-white
          rounded
          bg-active
        "
          :disabled="processing || loadingFare"
        >
          {{ processing ? "Please wait..." : "Proceed to payment" }}
        </button>
        <div
          class="
          w-11/12
          mx-auto
          mt-2
          font-medium
          text-center text-xxs text-dark-type-4
        "
        >
          By booking your ticket, you agree to our
          <a :href="refundPolicyLink" target="_blank" class="text-dark-type-3">refund policy</a>,
          <a :href="termsOfUseLink" target="_blank" class="text-dark-type-3">terms of service</a> and
          <a :href="privacyPolicyLink" target="_blank" class="text-dark-type-3">privacy policy</a>
        </div>
      </div>
    </template>

    <modal :show="showPaymentModal" @close="showPaymentModal = false">
      <payment-wizard
        :payment-information="paymentInformation"
        @payment-success="(bookings) => $emit('payment-success', bookings)"
        @close="onClosePaymentWizard"
      ></payment-wizard>
    </modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import flatPickr from "vue-flatpickr-component";
import PaymentWizard from "@/components/payment-wizard.vue";
import Modal from "@/components/Modal";
import { computeDiscountAmount } from "@/services/utils";
import axios from "@/services/axios";

export default {
  props: {
    paymentData: Object,
    processBooking: Function,
    favouriteBooking: Boolean,
    loadingFare: Boolean,
  },
  components: {
    flatPickr,
    PaymentWizard,
    Modal,
  },
  data() {
    return {
      showPaymentModal: false,
      processing: false,
      luggageCost: 0,
      totalAmount: 0,
      departureDate: null,
      appliedDiscount: null,
    };
  },
  created() {},
  computed: {
    ...mapGetters("app", ["wallet"]),
    ...mapGetters("auth", ["user"]),
    termsOfUseLink() {
      return `${process.env.VUE_APP_WEBSITE_URL}/terms`;
    },
    privacyPolicyLink() {
      return `${process.env.VUE_APP_WEBSITE_URL}/privacy-policy`;
    },
    refundPolicyLink() {
      return `${process.env.VUE_APP_WEBSITE_URL}/refund-policy`;
    },
    canCoverFees() {
      return parseInt(this.wallet.amount) >= this.paymentData.total;
    },
    firstRouteTotalFare() {
      if (this.paymentData.ticketType.toLowerCase() === "subscription") {
        const totalFare =
          this.paymentData.first_route.fare *
          this.paymentData.options.trip_days.length *
          this.paymentData.options.subscription_duration;

        return totalFare;
      }

      return this.paymentData.first_route.fare || 0;
    },
    secondRouteFare() {
      return this.paymentData.second_route.fare || 0;
    },
    payableAmount() {
      return this.appliedDiscount
        ? this.paymentData.total - this.appliedDiscount.discountAmount
        : this.paymentData.total;
    },
    paymentInformation() {
      return {
        ticketType: this.paymentData.ticketType,
        passengers: this.paymentData.passengers,
        pricePerPassenger: this.paymentData.pricePerPassenger,
        returnTripPricePerPassenger: this.secondRouteFare,
        luggageCost: this.luggageCost,
        total: this.paymentData.total,
        payableAmount: this.payableAmount,
        discountText: this.appliedDiscount?.discountText,
        processBooking: this.processBooking,
      };
    },
  },
  methods: {
    async checkForPromoCode(userId) {
      try {
        let promoCode = await axios
          .get(`users/${userId}/next-applicable-route-promo-code`)
          .then((res) => res?.data);
        if (promoCode) {
          promoCode = computeDiscountAmount(
            this.firstRouteTotalFare,
            promoCode,
            this.paymentData.ticketType
          );
        }
        return promoCode;
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async maybeTriggerPaymentModal(paymentData) {
      this.processing = true;
      if (!this.user) {
        return this.$router.push({
          name: "login",
          query: { redirect: this.$route.fullPath },
        });
      }

      if (this.favouriteBooking) {
        if (!this.departureDate) {
          this.$toast.error(
            "Invalid departure date, please select a new departure date for your booking"
          );
          return false;
        }
      }

      if (paymentData.options.route_type === "one-way") {
        const route = paymentData.first_route;
        const luggageQty = paymentData.route_details.luggage_quantity;
        if (!this.validateLuggageCount(route, luggageQty)) {
          this.$toast.error(
            `You're only allowed to a maximum of ${route.luggageConfigData.max_quantity} luggage for this route`
          );
          return false;
        }
        this.setLuggageCost(route, luggageQty);
        this.setTotal();
      }

      this.setTotal();

      // apply promo to booking payment
      const promoCodeData = await this.checkForPromoCode(this.user.id);
      this.appliedDiscount = promoCodeData ? promoCodeData : null;
      this.processing = false;
      this.showPaymentModal = true;
    },
    validateLuggageCount(route, qty) {
      if (Boolean(route.support_luggage) && qty > 1) {
        const luggageConfig = route.luggageConfigData;
        return parseInt(luggageConfig.max_quantity) >= parseInt(qty);
      }
      return true;
    },
    setTotal() {
      this.totalAmount =
        parseInt(this.paymentData.total) + parseInt(this.luggageCost);
    },
    setLuggageCost(route, qty) {
      if (Boolean(route.support_luggage) && qty > 1) {
        const { fee_type, amount } = route.luggageConfigData;
        if (fee_type === "fee_per_additional_luggage") {
          this.luggageCost = (qty - 1) * amount;
        } else if (fee_type === "flat_fee") {
          this.luggageCost = amount;
        } else {
          this.luggageCost = 0;
        }
        return;
      }
      this.luggageCost = 0;
    },
    emitUpdatedDate(event) {
      this.$emit("departure-updated", event);
    },
    onClosePaymentWizard() {
      this.showPaymentModal = false;
    },
  },
};
</script>
<style lang="scss">
.date-picker-control {
  height: 2.5rem;
}
.flat-picker {
  color: white;
  &::placeholder {
    font-size: 0.875rem;
    font-weight: normal;
  }
}
</style>

<style lang="scss" scoped>
.vs__dropdown-toggle {
  border: 0;
  background-color: white;
  padding-top: 6px;
  padding-bottom: 6px;
}

.vs__dropdown-menu {
  background-color: white;
  color: black;
}

.vs__dropdown-option {
  color: black;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;

  &--highlight {
    background: none;
  }
}

.vs__selected-options .vs__selected {
  color: black;
}

.vs--single .vs__selected {
  color: black;
}

.vs__selected {
  background: theme("colors.active");
  color: black;
  font-weight: 600;
  font-size: 0.875rem;
}

.vs__search::placeholder {
  color: black;
  font-size: 0.875rem;
}
</style>
